import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  BadgeStatus,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FilterBar,
  Input,
  KolomForm,
  Modal,
  PageHeader,
  Select,
  Textarea,
  FILTER_KOSONG,
  type KolomTabel,
  type NilaiFilter,
} from '@/components'
import { api, buatIdempotencyKey, pesanKesalahan } from '@/lib/api'
import { formatRupiah, formatTanggal } from '@/lib/format'
import type { Santri, Tagihan } from '@/lib/types'
import { useApi, usePaged, useAksi } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { useAuth } from '@/lib/auth'

const STATUS_TAGIHAN = [
  'DRAFT',
  'PENDING_APPROVAL',
  'UNPAID',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
  'CANCELLED',
  'IN_CORRECTION',
]

interface BarisForm {
  description: string
  quantity: string
  unitPrice: string
}

export default function TagihanPage() {
  const navigate = useNavigate()
  const { punyaIzin } = useAuth()
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({
    ...FILTER_KOSONG,
    dari: `${new Date().getFullYear()}-01-01`,
  })
  const [statusFilter, setStatusFilter] = useState('')
  const [cari, setCari] = useState('')

  const daftar = usePaged<Tagihan>('/invoices', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
      status: statusFilter || undefined,
      q: cari.trim() || undefined,
    },
  })

  const [modalBuat, setModalBuat] = useState(false)
  const [konfirmasiPosting, setKonfirmasiPosting] = useState<Tagihan | null>(null)
  const aksi = useAksi()

  const kolom = useMemo<KolomTabel<Tagihan>[]>(
    () => [
      {
        kunci: 'invoiceNumber',
        judul: 'Nomor',
        render: (b) => (
          <Link to={`/tagihan/${b.id}`} className="font-medium text-yayasan-800 hover:underline">
            {b.invoiceNumber}
          </Link>
        ),
      },
      {
        kunci: 'student',
        judul: 'Santri/Siswa',
        nilaiSort: (b) => b.student?.person?.fullName ?? '',
        render: (b) => (
          <div>
            <p className="font-medium text-netral-800">{b.student?.person?.fullName ?? b.studentId}</p>
            <p className="text-xs text-netral-500">{b.student?.studentNumber ?? ''}</p>
          </div>
        ),
      },
      {
        kunci: 'unit',
        judul: 'Unit',
        render: (b) => b.unit?.name ?? '—',
      },
      { kunci: 'invoiceDate', judul: 'Tanggal', render: (b) => formatTanggal(b.invoiceDate) },
      { kunci: 'dueDate', judul: 'Jatuh Tempo', render: (b) => formatTanggal(b.dueDate) },
      { kunci: 'totalAmount', judul: 'Total', rata: 'kanan', render: (b) => formatRupiah(b.totalAmount) },
      { kunci: 'paidAmount', judul: 'Dibayar', rata: 'kanan', render: (b) => formatRupiah(b.paidAmount) },
      {
        kunci: 'outstandingAmount',
        judul: 'Sisa',
        rata: 'kanan',
        render: (b) => <span className="font-medium text-red-700">{formatRupiah(b.outstandingAmount)}</span>,
      },
      { kunci: 'status', judul: 'Status', render: (b) => <BadgeStatus status={b.status} /> },
      {
        kunci: 'aksi',
        judul: 'Aksi',
        sortable: false,
        render: (b) => (
          <div className="flex flex-wrap gap-1.5">
            <Button ukuran="kecil" varian="garis" onClick={() => navigate(`/tagihan/${b.id}`)}>
              Detail
            </Button>
            {punyaIzin('invoice:approve', 'invoice:publish') && b.status === 'DRAFT' ? (
              <Button ukuran="kecil" onClick={() => setKonfirmasiPosting(b)}>
                Posting
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [navigate, punyaIzin],
  )

  return (
    <div>
      <PageHeader
        judul="Tagihan Santri/Siswa"
        deskripsi="Daftar tagihan beserta status pembayarannya. Tagihan draf harus diposting agar masuk ke jurnal."
        ekspor={{
          path: '/reports/receipts',
          namaDasar: `tagihan-${filter.dari || 'semua'}`,
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
        aksi={
          punyaIzin('invoice:create') ? <Button onClick={() => setModalBuat(true)}>+ Buat Tagihan</Button> : null
        }
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ kategori: false, program: false, fundId: false }}
        ekstra={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Status"
              placeholder="Semua status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              opsi={STATUS_TAGIHAN.map((s) => ({ value: s, label: s }))}
              className="min-w-40"
            />
            <Input
              label="Cari"
              placeholder="Nomor tagihan / nama santri"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
            />
          </div>
        }
      />

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b) => b.id}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong="Belum ada tagihan pada filter ini."
      />

      <FormBuatTagihan
        terbuka={modalBuat}
        onTutup={() => setModalBuat(false)}
        unit={opsiUnit}
        setelahBuat={() => {
          setModalBuat(false)
          daftar.muat()
        }}
      />

      <ConfirmDialog
        terbuka={konfirmasiPosting !== null}
        onTutup={() => setKonfirmasiPosting(null)}
        judul="Posting Tagihan"
        pesan={
          <>
            Posting tagihan <strong>{konfirmasiPosting?.invoiceNumber}</strong> sebesar{' '}
            <strong>{formatRupiah(konfirmasiPosting?.totalAmount ?? 0)}</strong>? Tagihan yang sudah
            diposting akan membentuk jurnal piutang dan tidak dapat diubah.
          </>
        }
        labelKonfirmasi="Posting Sekarang"
        memuat={aksi.memuat}
        onKonfirmasi={async () => {
          const id = konfirmasiPosting?.id
          if (!id) return
          const hasil = await aksi.jalankan(
            () => api.post(`/invoices/${id}/post`, {}, { idempotencyKey: buatIdempotencyKey() }),
            { sukses: 'Tagihan berhasil diposting.' },
          )
          if (hasil !== null) {
            setKonfirmasiPosting(null)
            daftar.muat()
          }
        }}
      />
    </div>
  )
}

function FormBuatTagihan({
  terbuka,
  onTutup,
  unit,
  setelahBuat,
}: {
  terbuka: boolean
  onTutup: () => void
  unit: Array<{ value: string; label: string }>
  setelahBuat: () => void
}) {
  const [unitId, setUnitId] = useState('')
  const [studentId, setStudentId] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10))
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10))
  const [notes, setNotes] = useState('')
  const [baris, setBaris] = useState<BarisForm[]>([{ description: '', quantity: '1', unitPrice: '' }])
  const [galat, setGalat] = useState<string | null>(null)
  const [memuat, setMemuat] = useState(false)

  const santri = useApi<unknown>('/students', { perPage: 200, unitId: unitId || undefined })
  const daftarSantri: Santri[] = Array.isArray(santri.data)
    ? (santri.data as Santri[])
    : ((santri.data as { items?: Santri[] } | null)?.items ?? [])

  const total = baris.reduce(
    (a, b) => a + (Number(b.quantity) || 0) * (Number(b.unitPrice.replace(/[^0-9.-]/g, '')) || 0),
    0,
  )

  const simpan = async () => {
    setGalat(null)
    if (!unitId) return setGalat('Unit wajib dipilih.')
    if (!studentId) return setGalat('Santri/siswa wajib dipilih.')
    const barisValid = baris.filter((b) => b.description.trim() && Number(b.unitPrice) > 0)
    if (barisValid.length === 0) return setGalat('Minimal satu baris tagihan dengan nilai lebih dari nol.')

    setMemuat(true)
    try {
      await api.post(
        '/invoices',
        {
          unitId,
          studentId,
          invoiceDate,
          dueDate,
          notes: notes.trim() || undefined,
          lines: barisValid.map((b) => ({
            description: b.description.trim(),
            quantity: Number(b.quantity) || 1,
            unitPrice: Number(b.unitPrice.replace(/[^0-9.-]/g, '')),
          })),
        },
        { idempotencyKey: buatIdempotencyKey() },
      )
      setelahBuat()
    } catch (e) {
      setGalat(pesanKesalahan(e))
    } finally {
      setMemuat(false)
    }
  }

  return (
    <Modal
      terbuka={terbuka}
      onTutup={onTutup}
      judul="Buat Tagihan Baru"
      deskripsi="Tagihan dibuat berstatus draf. Posting untuk membentuk jurnal piutang."
      lebar="max-w-3xl"
      footer={
        <>
          <Button varian="garis" onClick={onTutup} disabled={memuat}>
            Batal
          </Button>
          <Button onClick={() => void simpan()} memuat={memuat}>
            Simpan Tagihan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {galat ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {galat}
          </div>
        ) : null}

        <KolomForm kolom={2}>
          <Select
            label="Unit"
            placeholder="Pilih unit"
            opsi={unit}
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            required
          />
          <Select
            label="Santri/Siswa"
            placeholder={santri.memuat ? 'Memuat…' : 'Pilih santri'}
            opsi={daftarSantri.map((s) => ({
              value: s.id,
              label: `${s.studentNumber} — ${s.person?.fullName ?? s.id}`,
            }))}
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            required
            bantuan={santri.galat ? `Gagal memuat santri: ${santri.galat}` : undefined}
          />
          <Input
            type="date"
            label="Tanggal Tagihan"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            required
          />
          <Input
            type="date"
            label="Jatuh Tempo"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            required
          />
        </KolomForm>

        <Card padat className="p-3">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-netral-800">Baris Tagihan</h3>
            <Button
              ukuran="kecil"
              varian="garis"
              onClick={() => setBaris((b) => [...b, { description: '', quantity: '1', unitPrice: '' }])}
            >
              + Tambah Baris
            </Button>
          </div>
          <div className="space-y-2">
            {baris.map((b, i) => (
              <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_100px_160px_auto]">
                <Input
                  placeholder="Uraian (mis. SPP Bulan Juli)"
                  value={b.description}
                  onChange={(e) =>
                    setBaris((arr) => arr.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))
                  }
                />
                <Input
                  type="number"
                  min={1}
                  placeholder="Qty"
                  value={b.quantity}
                  onChange={(e) =>
                    setBaris((arr) => arr.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))
                  }
                />
                <Input
                  placeholder="Harga satuan"
                  inputMode="numeric"
                  value={b.unitPrice}
                  onChange={(e) =>
                    setBaris((arr) => arr.map((x, j) => (j === i ? { ...x, unitPrice: e.target.value } : x)))
                  }
                />
                <Button
                  varian="halus"
                  ukuran="kecil"
                  onClick={() => setBaris((arr) => (arr.length > 1 ? arr.filter((_, j) => j !== i) : arr))}
                >
                  Hapus
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-3 text-right text-sm font-semibold text-netral-800">
            Total: {formatRupiah(total)}
          </p>
        </Card>

        <Textarea label="Catatan" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
      </div>
    </Modal>
  )
}
